import { useContext, useEffect, useMemo, useState } from "react"
import { Button } from "components/ui/button"
import { Column } from "@tanstack/react-table"
import { DataTableColumnHeader } from "components/data-table/data-table-column-header"
import { cn } from "@/utils/shadcn-utils"
import { useDataTable } from "@/hooks/shadcn/useDataTable"
import { DataTable } from "components/data-table/data-table"
import { DataTableToolbar } from "components/data-table/data-table-toolbar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu"
import { useForm } from "react-hook-form"
import {
  ActionNotificationContext,
  ActionNotificationContextValeurType,
} from "@/context/ActionNotificationContext"
import { Ellipsis, Plus } from "lucide-react"
import { LegalPersonFormType } from "@/types/LegalPersonForm"
import LegalPersonModalForm from "@/views/clients/components/LegalPersonModalForm"
import { useOutletContext, useParams } from "react-router-dom"
import { getCompany } from "@/requests/companiesRequests"
import { CompanyType1 } from "@/types/CompaniesType"
import { InvoiceType } from "@/types/InvoicesType"

function ClientLegalRepresentatives() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleModalOpen = () => setIsModalOpen(true)
  const handleModalClose = () => {
    setIsModalOpen(false)
    setSelectedRepresentative(undefined)
  }
  const { company } = useOutletContext<{ company: CompanyType1, invoices: InvoiceType[] }>()

  const [selectedRepresentative, setSelectedRepresentative] = useState<LegalPersonFormType | undefined>()
  // const [representatives, setRepresentatives] = useState<LegalPersonFormType[]>( [] )

  // const { displayAlert } = useContext(
  //   ActionNotificationContext
  // ) as ActionNotificationContextValeurType


  const columns = useMemo(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: ({ column }: { column: Column<LegalPersonFormType, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Id"
            className="py-3 px-1"
          />
        ),
        cell: ({ row } :{row:any}) => {
          return (
            <div className="flex items-center gap-2 pl-2">
              <span>{String(row.index + 1).padStart(3, "0")}</span>
            </div>
          )
        },
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Id",
        },
        size: 50,
      },
      {
        id: "last_name",
        accessorKey: "last_name",
        header: ({ column }: { column: Column<LegalPersonFormType, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Nom"
            className="py-3 px-1"
          />
        ),
        cell: ({ row } :{row:any}) => (
          <div className="flex items-center gap-2 pl-2 uppercase">
            <span>{row.getValue("last_name") ?? "--"}</span>
          </div>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Nom",
          labelInColumnsAdjustment: "Nom",
          placeholder: "Rechercher ",
          variant: "text",
          priority: 8,
        },
        filterFn: (row: any, columnId: string, filterValue: string) => {
          // return true
          return `${row.original.first_name} ${row.original.last_name}`
            .toLowerCase()
            .includes(filterValue.toLowerCase())
        },
        size: 100,
      },
      {
        id: "first_name",
        accessorKey: "first_name",
        header: ({ column }: { column: Column<LegalPersonFormType, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Prénoms"
            className="py-3 px-1"
          />
        ),
        cell: ({ row } :{row:any}) => (
          <div className="flex items-center gap-2 pl-2">
            <span>{row.getValue("first_name") ?? "--"}</span>
          </div>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Prénoms",
          labelInColumnsAdjustment: "Prénoms",
          priority: 8,
        },
        size: 100,
      },
      {
        id: "email",
        accessorKey: "email",
        header: ({ column }: { column: Column<LegalPersonFormType, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Email"
            className="py-3 px-1"
          />
        ),
        cell: ({ row } :{row:any}) => (
          <div>{row.getValue("email") ?? "--"}</div>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Email",
        },
        size: 120,
      },
      {
        id: "phone_number",
        accessorKey: "phone_number",
        header: ({ column }: { column: Column<LegalPersonFormType, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Contact"
            className="py-3 px-1"
          />
        ),
        cell: ({ row } :{row:any}) => (
          <div className="flex items-center gap-2 pl-2">
            {/*<span>{0}</span>*/}
            {row.getValue("phone_number") ?? "--" }
          </div>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Contact",
        },
        size: 100,
      },
      {
        id: "position",
        accessorKey: "position",
        header: ({ column }: { column: Column<LegalPersonFormType, unknown> }) => (
          <DataTableColumnHeader
            column={column}
            title="Poste"
            className="py-3 px-1"
          />
        ),
        cell: ({ row } :{row:any}) => (
          <div className="flex items-center gap-2 pl-2">
            {row.getValue("position") ?? "--" }
          </div>
        ),
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
          label: "Poste",
        },
        size: 100,
      },
      // {
      //   id: "is_active",
      //   accessorKey: "is_active",
      //   header: ({ column }: { column: Column<LegalPersonFormType, unknown> }) => (
      //     <DataTableColumnHeader
      //       column={column}
      //       title="Statut"
      //       className="py-3 px-1"
      //     />
      //   ),
      //   cell: ({ row } :{row:any}) => {
      //     return (
      //       <div className="flex items-center gap-2 pl-2">
      //         <div
      //           className={cn(
      //             "tag",
      //             row.original.is_active ? "activeTag" : "inactiveTag"
      //           )}>
      //           {row.original.is_active ? "Actif": "Inactif"}
      //         </div>
      //       </div>
      //     )
      //   },
      //   // enableColumnFilter: true,
      //   enableSorting: true,
      //   meta: {
      //     label: "Statut",
      //     variant: "multiSelect",
      //     options: [
      //       {
      //         label: "Active",
      //         value: true,
      //       },
      //       {
      //         label: "Inactive",
      //         value: false,
      //       }
      //     ]
      //   },
      //   filterFn: (row: any, columnId: string, filterValue: boolean[]) => {
      //     return filterValue.includes(row.original.is_active);
      //   },
      //   size: 125,
      // },
      // {
      //   id: "actions",
      //   header: () => "Actions",
      //   cell: ({ row } :{row:any}) => {
      //     return (
      //       <DropdownMenu>
      //         <DropdownMenuTrigger asChild>
      //           <Button variant="ghost" size="icon">
      //             <Ellipsis />
      //             {/*<span className="sr-only">Actions</span>*/}
      //           </Button>
      //         </DropdownMenuTrigger>
      //         <DropdownMenuContent align="end">
      //           <DropdownMenuItem>
      //             <button
      //               type="button"
      //               className="w-full text-left"
      //               onClick={() => {
      //                 setSelectedRepresentative(row.original)
      //                 handleModalOpen()
      //               }}>
      //               Modifier le représentant
      //             </button>
      //           </DropdownMenuItem>
      //         </DropdownMenuContent>
      //       </DropdownMenu>
      //     )
      //   },
      //   size: 80,
      // },
    ],
    []
  )

  const table = useDataTable({
    data: company?.representatives,
    columns: columns,
    // initialColumnPinning: { right: ["actions"] },
  })


  return (
    <>
      <div className="card ">
        <DataTable
          table={table}
          noDataMessage="Aucun représentant défini"
          tableLayout="fixed">
          <DataTableToolbar table={table} rightAddon={
            <Button
              className="bg-blue-700 text-xs h-8 pl-2"
              onClick={() => handleModalOpen()}>
              <Plus /> Ajouter un représentant
            </Button>
          }/>
        </DataTable>
      </div>

      {
        isModalOpen &&
        <LegalPersonModalForm
          isModalOpen={isModalOpen}
          handleModalClose={handleModalClose}
          representative={selectedRepresentative}
        />
      }
    </>
  )
}

export default ClientLegalRepresentatives